import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ATTRIBUTES,
  getAttribute,
  type Attribute,
  type AttributeValue,
} from '../lib/attributes';
import { formatScaledCount } from '../lib/scaling';
import { useBlobStore } from '../state/blobStore';
import { useDashboardStore } from '../state/dashboardStore';
import { useSelectionStore } from '../state/selectionStore';
import {
  defaultStageFor,
  useFilterStore,
  useFilteredIndices,
  type FilteredResult,
  type FilterStage,
} from '../state/filterStore';

export type FilterPanelSelectionMode = 'all' | 'selection' | 'drawing';

type StagePatch = Partial<Omit<FilterStage, 'id' | 'kind' | 'attributeKey'>>;

export type FilterPanelBundle = {
  stages: FilterStage[];
  addStage: (stage: FilterStage) => void;
  updateStage: (id: string, patch: StagePatch) => void;
  removeStage: (id: string) => void;
  clearStages: () => void;
  filtered: FilteredResult | null;
  selectionMode: FilterPanelSelectionMode;
  onCreateDashboard?: (name: string) => void;
  suggestedDashboardName?: string;
};

type StageActions = Pick<
  FilterPanelBundle,
  'addStage' | 'updateStage' | 'removeStage'
>;

const StageActionsContext = createContext<StageActions | null>(null);

function useStageActions(): StageActions {
  const ctx = useContext(StageActionsContext);
  if (!ctx) {
    throw new Error('FilterPanel: StageActionsContext is missing');
  }
  return ctx;
}

export function useGlobalFilterBundle(): FilterPanelBundle {
  const stages = useFilterStore((s) => s.stages);
  const addStage = useFilterStore((s) => s.addStage);
  const updateStage = useFilterStore((s) => s.updateStage);
  const removeStage = useFilterStore((s) => s.removeStage);
  const clearStages = useFilterStore((s) => s.clearStages);
  const filtered = useFilteredIndices();
  const mode = useSelectionStore((s) => s.mode);
  const baseSelection = useSelectionStore((s) => s.baseSelection);
  const polygon = useSelectionStore((s) => s.polygon);
  const createDashboard = useDashboardStore((s) => s.createDashboard);
  const dashboardCount = useDashboardStore((s) => s.dashboards.length);
  const navigate = useNavigate();

  const selectionMode: FilterPanelSelectionMode =
    mode === 'selected' ? 'selection' : mode === 'drawing' ? 'drawing' : 'all';

  const onCreateDashboard = useCallback(
    (name: string) => {
      const stagesSnapshot: FilterStage[] = stages.map((stage) => {
        if (stage.kind === 'boolean') return { ...stage };
        if (stage.kind === 'categorical') {
          return { ...stage, value: stage.value.slice() };
        }
        return { ...stage, value: stage.value.slice() as typeof stage.value };
      });
      const baseSnapshot =
        baseSelection !== null ? new Uint32Array(baseSelection) : null;
      const polygonSnapshot =
        baseSnapshot !== null && polygon.length >= 3
          ? polygon.map((v) => [v[0], v[1]] as [number, number])
          : null;
      const dashboard = createDashboard(name, {
        stages: stagesSnapshot,
        baseSelection: baseSnapshot,
        polygon: polygonSnapshot,
      });
      navigate(`/dashboards/${dashboard.id}`, {
        state: { justCreated: true },
      });
    },
    [stages, baseSelection, polygon, createDashboard, navigate],
  );

  return {
    stages,
    addStage,
    updateStage,
    removeStage,
    clearStages,
    filtered,
    selectionMode,
    onCreateDashboard,
    suggestedDashboardName: `Dashboard ${dashboardCount + 1}`,
  };
}

function formatCount(n: number, sampleSize: number): string {
  return formatScaledCount(n, sampleSize);
}

function CategoricalEditor({
  stage,
  values,
}: {
  stage: FilterStage & { kind: 'categorical' };
  values: AttributeValue[];
}) {
  const { updateStage } = useStageActions();
  const selected = new Set(stage.value);
  const toggle = (v: number) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    updateStage(stage.id, { value: Array.from(next) } as StagePatch);
  };
  return (
    <div className="filter-editor">
      <div className="filter-editor__operator">
        <label>
          <input
            type="radio"
            name={`op-${stage.id}`}
            checked={stage.operator === 'in'}
            onChange={() =>
              updateStage(stage.id, { operator: 'in' } as StagePatch)
            }
          />
          in
        </label>
        <label>
          <input
            type="radio"
            name={`op-${stage.id}`}
            checked={stage.operator === 'not-in'}
            onChange={() =>
              updateStage(stage.id, { operator: 'not-in' } as StagePatch)
            }
          />
          not in
        </label>
      </div>
      <div className="filter-editor__chips">
        {values.map((v) => {
          const on = selected.has(v.value);
          return (
            <button
              key={v.value}
              type="button"
              className={`filter-chip${on ? ' filter-chip--on' : ''}`}
              onClick={() => toggle(v.value)}
            >
              {v.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function OrdinalEditor({
  stage,
  values,
}: {
  stage: FilterStage & { kind: 'ordinal' };
  values: AttributeValue[];
}) {
  const { updateStage } = useStageActions();
  const allValues = values.map((v) => v.value);
  const minBound = allValues[0] ?? 0;
  const maxBound = allValues[allValues.length - 1] ?? 0;
  const labelFor = (n: number) =>
    values.find((v) => v.value === n)?.label ?? String(n);

  const setOperator = (operator: 'in' | 'range') => {
    if (operator === 'range') {
      updateStage(stage.id, {
        operator: 'range',
        value: [minBound, maxBound],
      } as StagePatch);
    } else {
      updateStage(stage.id, {
        operator: 'in',
        value: allValues.slice(),
      } as StagePatch);
    }
  };

  if (stage.operator === 'range') {
    const [lo, hi] = stage.value as [number, number];
    return (
      <div className="filter-editor">
        <div className="filter-editor__operator">
          <label>
            <input
              type="radio"
              name={`op-${stage.id}`}
              checked
              onChange={() => setOperator('range')}
            />
            range
          </label>
          <label>
            <input
              type="radio"
              name={`op-${stage.id}`}
              checked={false}
              onChange={() => setOperator('in')}
            />
            in
          </label>
        </div>
        <div className="filter-editor__range">
          <label>
            min: {labelFor(lo)}
            <input
              type="range"
              min={minBound}
              max={maxBound}
              value={lo}
              onChange={(e) => {
                const next = Math.min(Number(e.target.value), hi);
                updateStage(stage.id, {
                  value: [next, hi],
                } as StagePatch);
              }}
            />
          </label>
          <label>
            max: {labelFor(hi)}
            <input
              type="range"
              min={minBound}
              max={maxBound}
              value={hi}
              onChange={(e) => {
                const next = Math.max(Number(e.target.value), lo);
                updateStage(stage.id, {
                  value: [lo, next],
                } as StagePatch);
              }}
            />
          </label>
        </div>
      </div>
    );
  }

  const selected = new Set(stage.value as number[]);
  const toggle = (v: number) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    updateStage(stage.id, {
      value: Array.from(next),
    } as StagePatch);
  };
  return (
    <div className="filter-editor">
      <div className="filter-editor__operator">
        <label>
          <input
            type="radio"
            name={`op-${stage.id}`}
            checked={false}
            onChange={() => setOperator('range')}
          />
          range
        </label>
        <label>
          <input
            type="radio"
            name={`op-${stage.id}`}
            checked
            onChange={() => setOperator('in')}
          />
          in
        </label>
      </div>
      <div className="filter-editor__chips">
        {values.map((v) => {
          const on = selected.has(v.value);
          return (
            <button
              key={v.value}
              type="button"
              className={`filter-chip${on ? ' filter-chip--on' : ''}`}
              onClick={() => toggle(v.value)}
            >
              {v.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BooleanEditor({
  stage,
  values,
}: {
  stage: FilterStage & { kind: 'boolean' };
  values: AttributeValue[];
}) {
  const { updateStage } = useStageActions();
  return (
    <div className="filter-editor">
      <div className="filter-editor__chips">
        {values.map((v) => (
          <button
            key={v.value}
            type="button"
            className={`filter-chip${stage.value === v.value ? ' filter-chip--on' : ''}`}
            onClick={() =>
              updateStage(stage.id, {
                value: v.value as 0 | 1,
              } as StagePatch)
            }
          >
            {v.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function summarizeStage(
  stage: FilterStage,
  attribute: Attribute,
  values: AttributeValue[],
): string {
  if (stage.kind === 'categorical') {
    if (stage.value.length === 0) return `${attribute.label} ${stage.operator} ∅`;
    const labels = stage.value
      .slice(0, 3)
      .map((v) => values.find((x) => x.value === v)?.label ?? String(v));
    const more = stage.value.length > 3 ? ` +${stage.value.length - 3}` : '';
    return `${attribute.label} ${stage.operator} {${labels.join(', ')}${more}}`;
  }
  if (stage.kind === 'ordinal') {
    if (stage.operator === 'range') {
      const [lo, hi] = stage.value as [number, number];
      const loLabel = values.find((v) => v.value === lo)?.label ?? String(lo);
      const hiLabel = values.find((v) => v.value === hi)?.label ?? String(hi);
      return `${attribute.label} ∈ [${loLabel}, ${hiLabel}]`;
    }
    return `${attribute.label} in {${(stage.value as number[]).length} values}`;
  }
  const lbl = values.find((v) => v.value === stage.value)?.label ?? String(stage.value);
  return `${attribute.label} = ${lbl}`;
}

function StageRow({
  stage,
  count,
  prevCount,
}: {
  stage: FilterStage;
  count: number;
  prevCount: number;
}) {
  const blob = useBlobStore((s) => s.blob);
  const { removeStage } = useStageActions();
  const [open, setOpen] = useState(false);
  const attribute = getAttribute(stage.attributeKey);
  const values = useMemo(
    () => (attribute && blob ? attribute.values(blob) : []),
    [attribute, blob],
  );
  if (!attribute) return null;
  const summary = summarizeStage(stage, attribute, values);
  const sampleSize = blob?.meta.personCount ?? 0;
  return (
    <li className="filter-stage">
      <div className="filter-stage__header">
        <button
          type="button"
          className="filter-stage__toggle"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          {summary}
        </button>
        <span className="filter-stage__count">
          {formatCount(count, sampleSize)} / {formatCount(prevCount, sampleSize)}
        </span>
        <button
          type="button"
          className="filter-stage__remove"
          aria-label={`Remove filter ${attribute.label}`}
          onClick={() => removeStage(stage.id)}
        >
          ×
        </button>
      </div>
      {open && (
        <div className="filter-stage__editor">
          {stage.kind === 'categorical' && (
            <CategoricalEditor stage={stage} values={values} />
          )}
          {stage.kind === 'ordinal' && (
            <OrdinalEditor stage={stage} values={values} />
          )}
          {stage.kind === 'boolean' && (
            <BooleanEditor stage={stage} values={values} />
          )}
        </div>
      )}
    </li>
  );
}

function CreateDashboardButton({
  onCreate,
  suggestedName,
  disabled,
  disabledReason,
  variant = 'compact',
}: {
  onCreate: (name: string) => void;
  suggestedName: string;
  disabled: boolean;
  disabledReason: string;
  variant?: 'compact' | 'primary';
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(suggestedName);

  if (!editing) {
    return (
      <button
        type="button"
        className={
          variant === 'primary'
            ? 'filter-panel__create-dashboard filter-panel__create-dashboard--primary'
            : 'filter-panel__create-dashboard'
        }
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
        onClick={() => {
          setName(suggestedName);
          setEditing(true);
        }}
      >
        Create dashboard
      </button>
    );
  }

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onCreate(trimmed);
    setEditing(false);
  };

  return (
    <form
      className="filter-panel__create-dashboard-form"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <input
        autoFocus
        type="text"
        className="filter-panel__create-dashboard-input"
        aria-label="Dashboard name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            setEditing(false);
          }
        }}
      />
      <button
        type="submit"
        className="filter-panel__create-dashboard-confirm"
        disabled={name.trim().length === 0}
      >
        Create
      </button>
      <button
        type="button"
        className="filter-panel__create-dashboard-cancel"
        onClick={() => setEditing(false)}
      >
        Cancel
      </button>
    </form>
  );
}

function AddFilter() {
  const blob = useBlobStore((s) => s.blob);
  const { addStage } = useStageActions();
  const [open, setOpen] = useState(false);
  if (!blob) return null;
  return (
    <div className="filter-add">
      <button
        type="button"
        className="filter-add__button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        + Add filter
      </button>
      {open && (
        <ul className="filter-add__menu" role="menu">
          {ATTRIBUTES.map((attr) => (
            <li key={attr.key}>
              <button
                type="button"
                role="menuitem"
                className="filter-add__option"
                onClick={() => {
                  const values = attr.values(blob).map((v) => v.value);
                  addStage(defaultStageFor(attr.key, attr.kind, values));
                  setOpen(false);
                }}
              >
                {attr.label}
                <span className="filter-add__kind">{attr.kind}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function FilterPanel({ bundle }: { bundle: FilterPanelBundle }) {
  const blob = useBlobStore((s) => s.blob);
  const sampleSize = blob?.meta.personCount ?? 0;

  const {
    stages,
    clearStages,
    filtered,
    selectionMode,
    onCreateDashboard,
    suggestedDashboardName,
  } = bundle;
  const createDashboardDisabled =
    stages.length === 0 && selectionMode !== 'selection';

  const stageActions = useMemo<StageActions>(
    () => ({
      addStage: bundle.addStage,
      updateStage: bundle.updateStage,
      removeStage: bundle.removeStage,
    }),
    [bundle.addStage, bundle.updateStage, bundle.removeStage],
  );

  if (!filtered) {
    return (
      <section className="filter-panel" aria-label="Filters">
        <header className="filter-panel__header">
          <h3 className="filter-panel__title">Filters</h3>
        </header>
        <p className="filter-panel__empty">
          Loading population… filters become available once the dataset is
          ready.
        </p>
      </section>
    );
  }

  const { baseCount, stageCounts, indices } = filtered;
  const prevCounts = [baseCount, ...stageCounts.slice(0, -1)];
  const finalCount = indices.length;

  return (
    <StageActionsContext.Provider value={stageActions}>
      <section className="filter-panel" aria-label="Filters">
        <header className="filter-panel__header">
          <h3 className="filter-panel__title">Filters</h3>
          <span className="filter-panel__base">
            {selectionMode === 'selection'
              ? 'Selection'
              : selectionMode === 'drawing'
                ? 'Drawing…'
                : 'All'}
            : {formatCount(baseCount, sampleSize)}
          </span>
          {stages.length > 0 && (
            <button
              type="button"
              className="filter-panel__clear"
              onClick={clearStages}
            >
              Clear all
            </button>
          )}
          {onCreateDashboard && (
            <CreateDashboardButton
              onCreate={onCreateDashboard}
              suggestedName={suggestedDashboardName ?? 'Dashboard'}
              disabled={createDashboardDisabled}
              disabledReason="Add a filter or draw a lasso selection to create a dashboard from a non-empty subset."
            />
          )}
        </header>
        {selectionMode === 'drawing' && (
          <p className="filter-panel__hint">
            Drawing a polygon. Click the map to add vertices · double-click or
            Enter to close · Esc to cancel.
          </p>
        )}
        {stages.length === 0 ? (
          <p className="filter-panel__empty">
            {selectionMode === 'selection'
              ? `Filtering ${formatCount(baseCount, sampleSize)} selected people. Add a filter to narrow further.`
              : selectionMode === 'drawing'
                ? 'Filters will apply once you close the polygon.'
                : `Showing all ${formatCount(baseCount, sampleSize)} people. Add a filter, or hold Space and draw a polygon on the map to focus on a region.`}
          </p>
        ) : (
          <>
            <ol className="filter-panel__stages">
              {stages.map((stage, idx) => (
                <StageRow
                  key={stage.id}
                  stage={stage}
                  count={stageCounts[idx]}
                  prevCount={prevCounts[idx]}
                />
              ))}
            </ol>
            {finalCount === 0 && (
              <p className="filter-panel__zero" role="status">
                No people match this combination of filters. Loosen or remove a
                filter to bring records back in.
              </p>
            )}
          </>
        )}
        <AddFilter />
        {onCreateDashboard && (
          <CreateDashboardButton
            onCreate={onCreateDashboard}
            suggestedName={suggestedDashboardName ?? 'Dashboard'}
            disabled={createDashboardDisabled}
            disabledReason="Add a filter or draw a lasso selection to create a dashboard from a non-empty subset."
            variant="primary"
          />
        )}
      </section>
    </StageActionsContext.Provider>
  );
}
